import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MapPin, Calendar, Home, Plus, ListTodo, Camera, ClipboardList } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import BottomNav from "@/components/BottomNav";
import { AddJobDialog } from "@/components/AddJobDialog";
import { VoiceTaskCapture } from "@/components/VoiceTaskCapture";
import logo from "@/assets/Struvent-Logo-2.png";
import type { Database } from "@/integrations/supabase/types";
import { useRolePreview } from "@/contexts/RolePreviewContext";
import { fetchJobsList, type JobData } from "@/hooks/useJobsQuery";

type AppRole = Database["public"]["Enums"]["app_role"];

const ROLES_THAT_CAN_ADD_JOBS: AppRole[] = ["PM", "Field Lead", "Full", "Admin"];

const Jobs = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<JobData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("In Progress");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [canAddJobs, setCanAddJobs] = useState(false);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [longPressJobId, setLongPressJobId] = useState<string | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);
  const { previewRole, isInPreviewMode } = useRolePreview();

  // Use preview role if in preview mode, otherwise use actual roles
  const effectiveRoles: AppRole[] = isInPreviewMode && previewRole 
    ? [previewRole as AppRole] 
    : userRoles;

  // Fetch user roles first, then fetch jobs with correct roles
  useEffect(() => {
    const init = async () => {
      const roles = await fetchUserRoles();
      await fetchJobs(roles);
    };
    init();
  }, []);

  // Re-fetch jobs when preview role changes
  useEffect(() => {
    if (userRoles.length > 0) {
      fetchJobs(effectiveRoles);
    }
  }, [previewRole]);

  // Calculate canAddJobs based on effective roles
  useEffect(() => {
    const hasPermission = effectiveRoles.some(role => ROLES_THAT_CAN_ADD_JOBS.includes(role));
    setCanAddJobs(hasPermission);
  }, [effectiveRoles]);

  // Keyboard shortcut: ⌘J to open add job dialog (only if user can add jobs)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "j" && canAddJobs) {
        e.preventDefault();
        setAddDialogOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canAddJobs]);

  const fetchUserRoles = async (): Promise<AppRole[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: roles, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching user roles:", error);
        return [];
      }

      if (roles && roles.length > 0) {
        const mappedRoles = roles.map(r => r.role as AppRole);
        setUserRoles(mappedRoles);
        return mappedRoles;
      } else {
        setUserRoles([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching user roles:", error);
      return [];
    }
  };

  const fetchJobs = async (roles?: AppRole[]) => {
    try {
      // Use provided roles or fall back to effectiveRoles
      const rolesToUse = roles ?? effectiveRoles;
      const { data, error } = await fetchJobsList(rolesToUse);

      if (error) throw error;
      setJobs(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-success text-white";
      case "In Progress":
        return "bg-primary text-white";
      case "On Hold":
        return "bg-warning text-white";
      default:
        return "bg-muted text-foreground";
    }
  };

  const filteredJobs = statusFilter === "all" 
    ? jobs 
    : jobs.filter(job => job.status === statusFilter);

  const getStatusCount = (status: string) => {
    if (status === "all") return jobs.length;
    return jobs.filter(job => job.status === status).length;
  };

  const getDaysRemaining = (endDate: string | null) => {
    if (!endDate) return null;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDaysRemainingColor = (days: number | null) => {
    if (days === null) return "";
    if (days <= 0) return "text-destructive font-bold";
    if (days <= 7) return "text-destructive font-semibold";
    if (days <= 14) return "text-warning font-medium";
    return "text-muted-foreground";
  };

  const getDaysRemainingBg = (days: number | null) => {
    if (days === null) return "";
    if (days <= 0) return "bg-destructive/10 border-destructive";
    if (days <= 7) return "bg-destructive/5 border-destructive/50";
    if (days <= 14) return "bg-warning/10 border-warning/50";
    return "bg-muted/50";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading jobs...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="bg-primary border-b border-primary sticky top-0 z-30 shadow-lg">
        <div className="p-3 sm:p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img 
              src={logo} 
              alt="Struvent" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain bg-white rounded-lg p-1.5"
            />
            <div>
              <h1 className="text-lg sm:text-xl font-heading font-bold text-primary-foreground">Jobs</h1>
              <p className="text-xs text-primary-foreground/80 mt-0.5">
                {jobs.length} active projects
              </p>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="secondary"
            onClick={() => navigate("/")}
            className="h-8 w-8 sm:h-9 sm:w-9 p-0"
          >
            <Home className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-4" data-tour="jobs-list">
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 h-auto gap-1">
            <TabsTrigger value="In Progress" className="text-xs sm:text-sm px-2">
              <span className="hidden sm:inline">In Progress</span>
              <span className="sm:hidden">Active</span>
              <span className="ml-1">({getStatusCount("In Progress")})</span>
            </TabsTrigger>
            <TabsTrigger value="Planning" className="text-xs sm:text-sm px-2">
              Plan ({getStatusCount("Planning")})
            </TabsTrigger>
            <TabsTrigger value="Scheduled" className="text-xs sm:text-sm px-2 hidden md:block">
              Sched ({getStatusCount("Scheduled")})
            </TabsTrigger>
            <TabsTrigger value="all" className="text-xs sm:text-sm px-2">
              All ({getStatusCount("all")})
            </TabsTrigger>
            <TabsTrigger value="Completed" className="text-xs sm:text-sm px-2">
              Done ({getStatusCount("Completed")})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">
                {statusFilter === "all" ? "No jobs yet" : `No ${statusFilter} jobs`}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredJobs.map((job) => (
            <div key={job.id} className="relative">
              <Card
                className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 rounded-xl border-border"
                onClick={() => {
                  if (longPressTriggered.current) {
                    longPressTriggered.current = false;
                    return;
                  }
                  navigate(`/job/${job.id}`);
                }}
                onTouchStart={() => {
                  longPressTriggered.current = false;
                  longPressTimer.current = setTimeout(() => {
                    longPressTriggered.current = true;
                    setLongPressJobId(job.id);
                  }, 500);
                }}
                onTouchEnd={() => {
                  if (longPressTimer.current) clearTimeout(longPressTimer.current);
                }}
                onTouchMove={() => {
                  if (longPressTimer.current) clearTimeout(longPressTimer.current);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setLongPressJobId(job.id);
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base sm:text-lg truncate">{job.name}</CardTitle>
                      {job.job_number && (
                        <CardDescription className="mt-1 text-xs sm:text-sm">
                          Job #{job.job_number}
                        </CardDescription>
                      )}
                    </div>
                    <Badge className={getStatusColor(job.status)} variant="secondary">
                      {job.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  {job.address && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="truncate">{job.address}</span>
                    </div>
                  )}
                  {job.start_date && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="truncate">
                        {new Date(job.start_date).toLocaleDateString()}
                        {job.end_date && ` - ${new Date(job.end_date).toLocaleDateString()}`}
                      </span>
                    </div>
                  )}
                  {job.projected_end_date && getDaysRemaining(job.projected_end_date) !== null && (
                    <div className={`flex items-center justify-between gap-2 text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border ${getDaysRemainingBg(getDaysRemaining(job.projected_end_date))}`}>
                      <span className="text-muted-foreground text-xs sm:text-sm">Target:</span>
                      <span className={getDaysRemainingColor(getDaysRemaining(job.projected_end_date))}>
                        {getDaysRemaining(job.projected_end_date)! <= 0 
                          ? "OVERDUE" 
                          : `${getDaysRemaining(job.projected_end_date)} days`}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Long-press / right-click quick actions popover */}
              {longPressJobId === job.id && (
                <div 
                  className="fixed inset-0 z-50"
                  onClick={() => setLongPressJobId(null)}
                  onTouchStart={() => setLongPressJobId(null)}
                >
                  <div 
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-popover border border-border rounded-xl shadow-xl p-2 w-52 z-50"
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <p className="px-3 py-1.5 text-xs font-semibold text-muted-foreground truncate">{job.name}</p>
                    <button
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg hover:bg-muted transition-colors"
                      onClick={() => { setLongPressJobId(null); navigate(`/job/${job.id}?tab=tasks`); }}
                    >
                      <ListTodo className="h-4 w-4 text-primary" /> View Tasks
                    </button>
                    <button
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg hover:bg-muted transition-colors"
                      onClick={() => { setLongPressJobId(null); navigate(`/job/${job.id}?tab=photos`); }}
                    >
                      <Camera className="h-4 w-4 text-primary" /> View Photos
                    </button>
                    <button
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg hover:bg-muted transition-colors"
                      onClick={() => { setLongPressJobId(null); navigate(`/job/${job.id}?tab=punch`); }}
                    >
                      <ClipboardList className="h-4 w-4 text-primary" /> Punch List
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {canAddJobs && !addDialogOpen && (
        <Button
          size="icon"
          onClick={() => setAddDialogOpen(true)}
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg bg-success hover:bg-success/90 text-white"
        >
          <Plus className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>
      )}

      {canAddJobs && (
        <AddJobDialog 
          open={addDialogOpen} 
          onOpenChange={setAddDialogOpen} 
          onJobCreated={fetchJobs} 
        />
      )}
      <VoiceTaskCapture onTaskCreated={() => fetchJobs(effectiveRoles)} />
      <BottomNav />
    </div>
  );
};

export default Jobs;
